type LRUNode<K, V> = {
  key: K;
  value: V;
  prev?: LRUNode<K, V>;
  next?: LRUNode<K, V>;
};

export class LRUMap<K, V> {
  #head: LRUNode<K, V> | undefined;

  #tail: LRUNode<K, V> | undefined;

  readonly #map = new Map<K, LRUNode<K, V>>();

  #size = 0;

  constructor(public readonly cap: number) {
    if (!Number.isInteger(cap) || cap < 1) {
      throw new RangeError(`LRUMap cap must be a positive integer, got ${cap}`);
    }
  }

  *[Symbol.iterator](): Generator<[K, V]> {
    let p = this.#head;
    while (p) {
      yield [p.key, p.value];
      p = p.next;
    }
  }

  has(key: K): boolean {
    return this.#map.has(key);
  }

  get(key: K): V | undefined {
    const node = this.#map.get(key);
    if (!node) return undefined;
    this.#bump(node);
    return node.value;
  }

  glance(key: K): V | undefined {
    return this.#map.get(key)?.value;
  }

  get size() {
    return this.#size;
  }

  clear() {
    this.#head = undefined;
    this.#tail = undefined;
    this.#map.clear();
    this.#size = 0;
  }

  set(key: K, value: V) {
    const existing = this.#map.get(key);
    if (existing) {
      this.#bump(existing);
      existing.value = value;
    } else {
      const created: LRUNode<K, V> = { key, value };
      this.#map.set(key, created);
      this.#bump(created);
      if (this.#size === this.cap) {
        this.#dropLast();
      }
      this.#size += 1;
    }
  }

  #bump(node: LRUNode<K, V>) {
    if (node === this.#head) return;

    const { next, prev } = node;

    if (prev) prev.next = next;
    if (next) next.prev = prev;

    if (node === this.#tail) {
      this.#tail = prev;
    }

    // eslint-disable-next-line no-param-reassign
    node.prev = undefined;
    // eslint-disable-next-line no-param-reassign
    node.next = this.#head;
    if (this.#head) this.#head.prev = node;
    this.#head = node;

    if (!this.#tail) this.#tail = node;
  }

  #dropLast() {
    if (!this.#tail) return;
    const removed = this.#tail;
    if (removed.prev) {
      removed.prev.next = undefined;
    }
    this.#tail = removed.prev;
    this.#size -= 1;
    this.#map.delete(removed.key);
  }
}

export class LRWriteMap<K, V> extends LRUMap<K, V> {
  get(key: K): V | undefined {
    return this.glance(key);
  }
}
